<script lang="ts">
  import { t } from '$lib/i18n';

  import Input from '$components/ui/input.svelte';
  import Select from '$components/ui/select/select.svelte';
  import Textarea from '$components/ui/textarea.svelte';
  import RadioButton from '$components/ui/radio-button.svelte';
  import Button from '$components/ui/button.svelte';
  import { drawer } from '$lib/stores/drawer';
  import type { CustomFieldsStoryblok } from '$types/bloks';
  import { formatCurrency } from '$lib/utils/currency';
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { getStoryblok, getStoryblokCacheValue } from '$lib/storyblok';
  import { page } from '$app/stores';
  import { lang } from '$lib/lang';
  import Skeleton from '$components/ui/skeleton.svelte';
  import { createReview } from '$lib/yotpo';
  import { toaster } from '$lib/toaster';
  import ShopifyImg from '$components/shopify-img.svelte';

  export let product: {
    id: string;
    title: string;
    category: string;
    price: number;
    currency: string;
    image: string;
  };

  const isCustomFields = (content: unknown): content is CustomFieldsStoryblok => {
    return (
      typeof content === 'object' &&
      content !== null &&
      'component' in content &&
      content.component === 'custom_fields'
    );
  };

  const storyblok = getStoryblok();
  $: client = createQuery<CustomFieldsStoryblok['form']>({
    staleTime: Infinity,
    queryKey: ['getReviewCustomFields'],
    queryFn: async () => {
      const res = await storyblok.get(`cdn/stories/config/review-slugs`, {
        cv: getStoryblokCacheValue($page.data.version),
        version: $page.data.version,
        language: $lang
      });

      if (res.data.story.content && isCustomFields(res.data.story.content)) {
        return res.data.story.content.form;
      } else {
        return [];
      }
    }
  });

  const queryClient = useQueryClient();
  $: submit = createMutation<void, unknown, Parameters<typeof createReview>[0]>({
    mutationFn: async (vars) => {
      return await createReview(vars);
    },
    onSuccess: () => {
      drawer.close();
      queryClient.invalidateQueries(['reviewBottomline', product.id]);
    }
  });
</script>

<div class="m-4 flex gap-3 rounded-md bg-highlight p-2">
  <ShopifyImg
    class="h-16 w-16 rounded-sm border bg-primary/2 object-contain"
    alt
    src={product.image}
    widths={[64]}
    sizes="64px"
  />
  <div class="flex flex-col py-0.5">
    <div class="flex-1">
      <p class="font-semibold">{product.title}</p>
      <p class="text-primary/40">{product.category}</p>
    </div>
    <p class="mt-1 font-semibold">{$formatCurrency(product.currency, product.price)}</p>
  </div>
</div>
<form
  on:submit={(e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const review = Object.fromEntries(data.entries());
    const { name, email, score, title, content, ...customFields } = review;

    toaster.promise(
      $submit.mutateAsync({
        sku: product.id,
        title: product.title,
        user: {
          name: name.toString(),
          email: email.toString()
        },
        review: {
          score: Number(score),
          title: title.toString(),
          content: content.toString(),
          customFields
        }
      }),
      {
        loading: {
          type: 'loading',
          title: $t('reviews.toast.loading.title')
        },
        success: {
          type: 'success',
          title: $t('reviews.toast.success.title'),
          description: $t('reviews.toast.success.description')
        },
        error: {
          type: 'error',
          title: $t('reviews.toast.error.title'),
          description: $t('error.unexpected.description')
        }
      }
    );
  }}
>
  <div class="p-4">
    <div class="flex flex-col gap-5">
      <Input
        name="name"
        label={$t('reviews.modal.name.label')}
        type="text"
        placeholder={$t('reviews.modal.name.placeholder')}
        required
        minlength={1}
      />
      <Input
        type="email"
        name="email"
        label={$t('reviews.modal.email.label')}
        placeholder={$t('reviews.modal.email.placeholder')}
        required
        minlength={1}
      />
    </div>

    <div class="mt-5 flex flex-col gap-5 border-t pt-5">
      <div>
        <p class="mb-2 text-sm font-semibold">{$t('reviews.modal.score')}</p>
        <!-- <Select class="w-full" name="score" required min="1" max="5" value="5">
          <option value="1">★</option>
          <option value="2">★★</option>
          <option value="3">★★★</option>
          <option value="4">★★★★</option>
          <option value="5">★★★★★</option>
        </Select> -->
        <Select
          required
          name="score"
          class="w-full"
          items={[
            { value: '1', label: '★' },
            { value: '2', label: '★★' },
            { value: '3', label: '★★★' },
            { value: '4', label: '★★★★' },
            { value: '5', label: '★★★★★' }
          ]}
          value={'5'}
        />
      </div>
      <div>
        <div>
          <Input
            type="text"
            name="title"
            label={$t('reviews.modal.title')}
            placeholder={$t('reviews.modal.title_placeholder')}
            required
          />
        </div>
      </div>
      <div>
        <Textarea
          name="content"
          label={$t('reviews.modal.review_title')}
          placeholder={$t('reviews.modal.review_placeholder')}
          required
        />
        <p class="mt-2 text-sm text-primary/40">
          {$t('reviews.modal.review_label')}
        </p>
      </div>
    </div>

    {#if $client.isLoading}
      <div class="mt-5 flex flex-col gap-3">
        {#each Array(3) as _}
          <Skeleton class="h-10 w-full rounded-md" />
        {/each}
      </div>
    {:else if $client.data && $client.data.length > 0}
      <div class="mt-5 flex flex-col gap-5 border-t pt-5">
        {#each $client.data as field}
          <div class="flex flex-col gap-2">
            <p class="mb-1 text-sm font-semibold">{field.title}</p>
            <div class="flex flex-wrap gap-2">
              {#each field.options || [] as option}
                {#if field.slug && option.text}
                  <RadioButton
                    required
                    name={field.slug}
                    label={option.text}
                    value={option.text}
                    id={field.slug + option.text}
                  />
                {/if}
              {/each}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>

  <div class="static lg:sticky bottom-0 mt-5 flex gap-4 border-t bg-background p-4">
    <Button name="save" disabled={$submit.isLoading} size="sm" type="submit" class="flex-1"
      >{$t('reviews.modal.save')}</Button
    >
    <Button
      name="cancel"
      size="sm"
      type="button"
      variant="ghost"
      class="flex-1"
      on:click={() => drawer.close()}>{$t('reviews.modal.cancel')}</Button
    >
  </div>
</form>
